import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import {
  Observable,
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  of,
  share,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { FeatureToggles } from '../enums/feature-toggles.enum';
import { LogEvent } from '../enums/log-event';
import { FeatureToggleService } from './feature-toggle.service';
import { LoggerService } from './logger.service';
import { StartService } from './start.service';

export type InactivityTrigger = 'keydown' | 'click' | 'scroll' | 'tabVisible' | 'tabHidden';
export type InactivityTimeouts = Record<InactivityTrigger, number>;

const MINUTE_MS = 60 * 1000;

/** DOM events that count as the user interacting with the app. */
const INTERACTION_EVENTS = ['keydown', 'click', 'scroll'] as const;

/** Silence (ms) after each trigger before the user is flagged idle. */
export const DEFAULT_INACTIVITY_TIMEOUTS: InactivityTimeouts = {
  keydown: 10 * MINUTE_MS,
  click: 10 * MINUTE_MS,
  scroll: 10 * MINUTE_MS,
  /** No-interaction countdown armed on start and whenever the user returns to the tab. */
  tabVisible: 10 * MINUTE_MS,
  /** Time away from the tab before idle. */
  tabHidden: 10 * MINUTE_MS,
};

export const INACTIVITY_TIMEOUTS = new InjectionToken<InactivityTimeouts>(
  'INACTIVITY_TIMEOUTS',
  {
    providedIn: 'root',
    factory: () => DEFAULT_INACTIVITY_TIMEOUTS,
  }
);

@Injectable({
  providedIn: 'root',
})
export class UserInactivityService {
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly timeouts = inject(INACTIVITY_TIMEOUTS);
  private readonly logger = inject(LoggerService);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly startService = inject(StartService);

  /**
   * True while the user is idle: no interaction for the trigger's timeout, or the
   * tab hidden for `tabHidden` (whichever comes first). Emits `false` on subscribe,
   * then only on transitions. Listeners attach on the first subscriber and are
   * removed once the last one unsubscribes.
   *
   * Behind `enable_user_inactivity_detection`: while the toggle is off (or not yet
   * resolved) no listeners are attached and the user is never flagged idle.
   */
  public readonly idle$: Observable<boolean> = this.detectionEnabled$().pipe(
    distinctUntilChanged(),
    switchMap((enabled) => (enabled ? this.detectIdle$() : of(false))),
    startWith(false),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Waits for `sessionReady$`: AppComponent subscribes before login, and the toggle
   * is resolved against the session's companies.
   */
  private detectionEnabled$(): Observable<boolean> {
    return defer(() => this.startService.sessionReady$).pipe(
      take(1),
      switchMap(() =>
        this.featureToggleService.someCompanyCanAccess(
          FeatureToggles.ENABLE_USER_INACTIVITY_DETECTION
        )
      )
    );
  }

  private detectIdle$(): Observable<boolean> {
    return new Observable<boolean>((subscriber) =>
      // Listeners and timers live outside the zone so scroll/keydown don't trigger
      // app-wide change detection; only state changes re-enter it.
      this.ngZone.runOutsideAngular(() =>
        this.buildIdle$().subscribe((idle) =>
          this.ngZone.run(() => subscriber.next(idle))
        )
      )
    );
  }

  private buildIdle$(): Observable<boolean> {
    const hidden$ = fromEvent(this.document, 'visibilitychange').pipe(
      map(() => this.isTabHidden()),
      share()
    );

    // Restarted by every interaction and by the user coming back to the tab.
    const interactionIdle$ = merge(
      ...INTERACTION_EVENTS.map((event) =>
        // Capture phase: `scroll` doesn't bubble out of inner containers, and handlers
        // calling stopPropagation would otherwise hide clicks/keys.
        fromEvent(this.document, event, { capture: true, passive: true }).pipe(
          map(() => event)
        )
      ),
      hidden$.pipe(
        filter((hidden) => !hidden),
        map((): InactivityTrigger => 'tabVisible')
      )
    ).pipe(
      startWith<InactivityTrigger>('tabVisible'),
      switchMap((trigger) => this.countdown(this.timeouts[trigger]))
    );

    // Only runs while the tab is hidden.
    const hiddenIdle$ = hidden$.pipe(
      startWith(this.isTabHidden()),
      switchMap((hidden) =>
        hidden ? this.countdown(this.timeouts.tabHidden) : of(false)
      )
    );

    return combineLatest([interactionIdle$, hiddenIdle$]).pipe(
      map(([interactionIdle, hiddenIdle]) => interactionIdle || hiddenIdle),
      distinctUntilChanged(),
      tap((idle) =>
        this.logger.log(
          idle ? LogEvent.LIVECHAT_USER_IDLE : LogEvent.LIVECHAT_USER_ACTIVE
        )
      )
    );
  }

  /** `false` right away, `true` once `ms` elapse. */
  private countdown(ms: number): Observable<boolean> {
    return timer(ms).pipe(
      map(() => true),
      startWith(false)
    );
  }

  private isTabHidden(): boolean {
    return this.document.visibilityState === 'hidden';
  }
}
